import { styled } from "@linaria/react";
import React, { useMemo } from "react";
import InputV2 from "../Input/InputV2";
import { color } from "..";
import classNames from "classnames";
import { fonts, spacings } from "../Theme";
import Text from "../Text";

const SLIDER_GRADIENT = `
  linear-gradient(
      to right,
      ${color.lightNitrogen500_Main} 0%,
      ${color.lightNitrogen500_Main} var(--slider-value),
      ${color.titanium200} var(--slider-value),
      ${color.titanium200} 100%
    );
`;

const StyledRange = styled.input`
  -webkit-appearance: none; /* Hides the slider so that custom slider can be made */
  width: 100%; /* Specific width is required for Firefox. */
  background: transparent; /* Otherwise white in Chrome */

  height: 2px;

  &::-webkit-slider-thumb {
    /* reset */
    -webkit-appearance: none;

    /* styling */
    height: 16px;
    width: 16px;
    border-radius: 10px;
    border: none;
    background: ${color.lightNitrogen500_Main};
    cursor: pointer;
    margin-top: -7px; /* You need to specify a margin in Chrome, but in Firefox and IE it is automatic */
  }

  &::-moz-range-thumb {
    /* reset */
    -webkit-appearance: none;

    /* styling */
    height: 16px;
    width: 16px;
    border-radius: 10px;
    background: ${color.lightNitrogen500_Main};
    border: none;
    cursor: pointer;
  }

  &:hover&::-webkit-slider-thumb,
  &:focus&::-webkit-slider-thumb {
    background: ${color.lightNitrogen600};
  }
  &:hover&::-moz-range-thumb,
  &:focus&::-moz-range-thumb {
    background: ${color.lightNitrogen600};
  }

  &.isInitialValue {
    &::-webkit-slider-thumb {
      background: ${color.hydrogen};
      border: 1px solid ${color.lightNitrogen500_Main};
    }
    &::-moz-range-thumb {
      background: ${color.hydrogen};
      border: 1px solid ${color.lightNitrogen500_Main};
    }

    &:hover&::-webkit-slider-thumb,
    &:focus&::-webkit-slider-thumb {
      background: ${color.hydrogen};
      border: 1px solid ${color.lightNitrogen600};
    }
    &:hover&::-moz-range-thumb,
    &:focus&::-moz-range-thumb {
      background: ${color.hydrogen};
      border: 1px solid ${color.lightNitrogen600};
    }
  }

  &:focus {
    /* reset */
    outline: none; /* Removes the blue border. You should probably do some kind of focus styling for accessibility reasons though. */
  }

  &:focus::-webkit-slider-runnable-track {
    background: ${SLIDER_GRADIENT};
    height: 2px;
  }

  &::-webkit-slider-runnable-track {
    width: 100%;
    height: 2px;
    cursor: pointer;
    background: ${SLIDER_GRADIENT};
  }

  &::-moz-range-track {
    width: 100%;
    height: 2px;
    cursor: pointer;
    background: ${SLIDER_GRADIENT};
  }
`;

const RangeContainer = styled.div`
  width: 100%;
  display: flex;
  gap: ${spacings.xs};
  align-items: center;
`;

const RangeWithBounds = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacings.xs};
  width: 100%;
`;

const SliderContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const InitialValueContainer = styled.div`
  height: 0px;
  transform: translateY(-26px);
  z-index: -1;
`;

const InitialValue = styled.div`
  position: relative;
  width: 16px;
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  align-content: center;
  align-items: center;
  left: calc((100% - 16px) / (var(--max) - var(--min)) * (var(--initial-value) - var(--min)));
`;

const Marker = styled.div`
  width: 0px;
  height: 5px;
  border: 1px solid ${color.titanium200};
  border-top-left-radius: 1px;
  border-top-right-radius: 1px;
`;

const MarkerValue = styled.div`
  font: ${fonts.body3};
  color: ${color.titanium200};
  margin-top: 3px;
`;

const SliderInput = styled(InputV2)`
  flex: 0 0 60px;
  width: 60px;

  input {
    padding-right: ${spacings.s};
    text-align: center;
  }
`;

export interface SliderProps extends React.InputHTMLAttributes<HTMLInputElement> {
  inputPosition?: "before" | "after";
  showBounds?: boolean;
  showInitialMarker?: boolean;
}

const Slider = ({
  className,
  inputPosition = "after",
  showBounds = true,
  showInitialMarker = true,
  ...props
}: SliderProps) => {
  const rangeRef = React.useRef<HTMLInputElement>(null);
  const initialValueContainerRef = React.useRef<HTMLDivElement>(null);
  const { onChange, defaultValue, value, min, max } = props;
  const [currentValue, setCurrentValue] = React.useState(value ?? defaultValue ?? min ?? 0);

  const internalValues = useMemo(
    () => ({
      initialValue: defaultValue ?? value ?? min ?? 0,
      min: min ?? 0,
      max: max ?? 100,
    }),
    [defaultValue, value, min, max],
  );

  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setCurrentValue(e.target.value);
      onChange?.(e);
    },
    [onChange],
  );

  React.useEffect(() => {
    if (value !== undefined) setCurrentValue(value);
  }, [value]);

  React.useLayoutEffect(() => {
    const currentRangeState = rangeRef.current;
    const initialValueContainerState = initialValueContainerRef.current;

    if (currentRangeState) {
      window.requestAnimationFrame(() => {
        const range = Number(currentRangeState.max) - Number(currentRangeState.min);
        const value = ((Number(currentValue) - Number(currentRangeState.min)) * 100) / range;
        currentRangeState.style.setProperty("--slider-value", `${value}%`);
        if (initialValueContainerState) {
          initialValueContainerState.style.setProperty("--max", `${internalValues.max}`);
          initialValueContainerState.style.setProperty("--min", `${internalValues.min}`);
          initialValueContainerState.style.setProperty(
            "--initial-value",
            `${internalValues.initialValue}`,
          );
        }
      });
    }
  }, [currentValue, internalValues]);

  const valueInput = (
    <SliderInput
      type="number"
      onChange={handleChange}
      min={internalValues.min}
      max={internalValues.max}
      step={props.step}
      value={currentValue}
      aria-label={props["aria-label"]}
      aria-labelledby={props["aria-labelledby"]}
      revertable={false}
    />
  );

  return (
    <RangeContainer className={className}>
      {inputPosition === "before" && valueInput}
      <RangeWithBounds>
        {showBounds && <Text type="body1">{internalValues.min}</Text>}
        <SliderContainer>
          <StyledRange
            ref={rangeRef}
            type="range"
            {...props}
            onChange={handleChange}
            value={currentValue}
            min={internalValues.min}
            max={internalValues.max}
            className={classNames({
              isInitialValue: Number(currentValue) === Number(internalValues.initialValue),
            })}
          />
          {showInitialMarker && (
            <InitialValueContainer ref={initialValueContainerRef}>
              <InitialValue>
                <MarkerValue>{internalValues.initialValue}</MarkerValue>
                <Marker />
              </InitialValue>
            </InitialValueContainer>
          )}
        </SliderContainer>
        {showBounds && <Text type="body1">{internalValues.max}</Text>}
      </RangeWithBounds>
      {inputPosition === "after" && valueInput}
    </RangeContainer>
  );
};

export default React.memo(Slider);
